import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useCapabilityCreate } from '@/hooks/use-entity-mutations';
import { getCapabilityColors } from '@/lib/capability-colors';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/telemetry';
import type { CreatedCapability } from '../CreateCapabilityDrawer';
import { WizardStepAppInfo } from '@/pages/integrations/components/wizard-step-app-info';
import { WizardStepAuth } from '@/pages/integrations/components/wizard-step-auth';
import { WizardStepMethods } from '@/pages/integrations/components/wizard-step-methods';
import {
    createDefaultFormData,
    toIntegrationManifest,
    type IntegrationFormData,
} from '@/types/integration-form-types';

const WIZARD_STEPS = [
    { id: 'app_info', title: 'App Information', description: 'Name, URL, and general details' },
    { id: 'auth', title: 'Authorization', description: 'Authentication type and configuration' },
    { id: 'methods', title: 'Methods', description: 'API endpoints and parameter mapping' },
] as const;

const TOTAL_STEPS = WIZARD_STEPS.length;

export interface IntegrationCreateWizardProps {
    appKey: string;
    onClose: () => void;
    onSuccess?: (entity: CreatedCapability) => void;
}

export function IntegrationCreateWizard({
    appKey,
    onClose,
    onSuccess,
}: IntegrationCreateWizardProps) {
    const createCapability = useCapabilityCreate(appKey);
    const colors = getCapabilityColors('integration');
    const TypeIcon = colors.icon;

    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<IntegrationFormData>(createDefaultFormData());
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        setErrors({});
    }, [currentStep]);

    const validateStep = (step: number): boolean => {
        const errs: Record<string, string> = {};

        if (step === 0) {
            if (!formData.appInfo.name.trim()) errs.name = 'App name is required';
            if (!formData.appInfo.baseUrl.trim()) errs.baseUrl = 'Base URL is required';
        }

        if (step === 1) {
            const auth = formData.auth;
            const enabledTypes = auth.enabledAuthTypes ?? [];
            if (enabledTypes.includes('api_key') && (auth.apiKeyDelivery === 'query' || auth.apiKeyDelivery === 'header')) {
                if (!auth.apiKeyParamName.trim()) errs.apiKeyParamName = 'Parameter name is required';
            }
            if (enabledTypes.includes('oauth2')) {
                if (!auth.accessTokenUrl.trim()) errs.accessTokenUrl = 'Access Token URL is required';
                if (auth.oauth2GrantType === 'authorization_code' && !auth.authorizationUrl.trim()) {
                    errs.authorizationUrl = 'Authorization URL is required';
                }
            }
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const goNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS - 1));
        }
    };

    const goBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    const handleSubmit = async () => {
        if (!validateStep(currentStep)) return;

        try {
            const { manifest, tags, description } = toIntegrationManifest(formData);

            const slug = formData.appInfo.name
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');

            const payload = {
                name: `integration.${slug}`,
                display_name: formData.appInfo.name,
                type: 'integration',
                description: description || null,
                tags: ['integration', ...tags],
                type_config: manifest,
            };

            logger.info('IntegrationCreateWizard:handleSubmit', { name: payload.name });
            const result = await createCapability.mutateAsync(payload);

            toast.success(`Integration "${formData.appInfo.name}" created`);
            if (onSuccess && result && typeof result === 'object' && 'name' in result) {
                onSuccess(result as CreatedCapability);
            }
            onClose();
        } catch (err: unknown) {
            logger.error('IntegrationCreateWizard:handleSubmit', { error: err });
            const msg = err instanceof Error ? err.message : 'Failed to create integration';
            toast.error(msg);
        }
    };

    const isLastStep = currentStep === TOTAL_STEPS - 1;
    const canGoNext = currentStep === 0
        ? formData.appInfo.name.trim().length > 0 && formData.appInfo.baseUrl.trim().length > 0
        : true;
    const stepTitle = WIZARD_STEPS[currentStep]?.title ?? '';

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
            {/* Header */}
            <div className="relative shrink-0 border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={cn('flex h-10 w-10 items-center justify-center rounded-full', colors.bgLight)}>
                            <TypeIcon className={cn('h-5 w-5', colors.text)} />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-slate-900">
                                Add New Integration
                            </h2>
                            <p className="text-sm text-slate-500">
                                Step {currentStep + 1} of {TOTAL_STEPS}: {stepTitle}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Step Indicators */}
                <div className="mt-3 flex items-center gap-2 px-16 pt-4">
                    {WIZARD_STEPS.map((step, index) => (
                        <div key={step.id} className="flex items-center">
                            <div
                                className={cn(
                                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                                    index < currentStep
                                        ? `${colors.bgLight.replace('100', '500')} bg-violet-500 text-white`
                                        : index === currentStep
                                            ? `${colors.bgLight} ${colors.text} ring-2 ring-violet-500`
                                            : 'bg-slate-100 text-slate-400',
                                )}
                            >
                                {index < currentStep ? <Check size={16} /> : index + 1}
                            </div>
                            {index < WIZARD_STEPS.length - 1 && (
                                <div
                                    className={cn(
                                        'mx-2 h-0.5 w-12 transition-colors',
                                        index < currentStep ? 'bg-violet-500' : 'bg-slate-200',
                                    )}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto bg-white px-6 py-4">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                    >
                        {currentStep === 0 && (
                            <WizardStepAppInfo
                                value={formData.appInfo}
                                onChange={(appInfo) => setFormData({ ...formData, appInfo })}
                                errors={errors}
                            />
                        )}
                        {currentStep === 1 && (
                            <WizardStepAuth
                                value={formData.auth}
                                onChange={(auth) => setFormData({ ...formData, auth })}
                            />
                        )}
                        {currentStep === 2 && (
                            <WizardStepMethods
                                value={formData.methods}
                                onChange={(methods) => setFormData({ ...formData, methods })}
                                appKey={appKey}
                            />
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
                <div>
                    {currentStep > 0 && (
                        <button
                            type="button"
                            onClick={goBack}
                            disabled={createCapability.isPending}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                        >
                            <ChevronLeft size={16} />
                            Back
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={createCapability.isPending}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    {isLastStep ? (
                        <button
                            type="button"
                            onClick={() => void handleSubmit()}
                            disabled={createCapability.isPending || !formData.appInfo.name.trim()}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {createCapability.isPending ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    Create Integration
                                    <Check size={16} />
                                </>
                            )}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={goNext}
                            disabled={!canGoNext}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Next
                            <ChevronRight size={16} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
